// apps/client/src/game/interactions/mailbox.ts (M1-T17) — A at a plot's mailbox: your own opens your mail, a friend's opens the
// writer addressed to them. Plots nobody lives on (and villagers' plots, M2) are skipped.
import { tuning } from '@whiskerton/content';
import type { ResidentId, Vec2, WorldObject } from '@whiskerton/shared';
import type { GameStore } from '../../state/types';
import type { Candidate, Interaction, InteractCtx } from '../interact';

const PREFIX = 'mailbox:';
/** Who a plot's mailbox belongs to: 'mine', a friend's resident id, or null when no resident lives there. */
function ownerOf(store: GameStore, plot: number): 'mine' | ResidentId | null {
  const me = store.you ? store.residents[store.you.resident] : undefined;
  if (me && me.plot === plot) return 'mine';
  return Object.values(store.residents).find((r) => r.plot === plot)?.id ?? null;
}
function candidate(store: GameStore, plot: number, at: Vec2): Candidate | null {
  const owner = ownerOf(store, plot);
  if (!owner) return null;
  return { id: `${PREFIX}${plot}`, at, verb: owner === 'mine' ? 'mail.verbRead' : 'mail.verbWrite', priority: 65 };
}

export const interaction: Interaction = {
  id: 'mailbox',
  find(ctx: InteractCtx) {
    const map = ctx.store.map;
    if (ctx.space !== 'out' || !map) return null;
    const fx = Math.sin(ctx.f), fy = Math.cos(ctx.f);
    let best: { plot: number; d: number; at: Vec2 } | null = null;
    for (const p of map.plots) {
      const at = { x: p.mailbox.x + 0.5, y: p.mailbox.y + 0.5 }, dx = at.x - ctx.pos.x, dy = at.y - ctx.pos.y, d = Math.hypot(dx, dy);
      if (d > tuning.town.interactRange || (d > 0.6 && (dx * fx + dy * fy) / d < 0.2)) continue;
      if (!ownerOf(ctx.store, p.index)) continue;
      if (!best || d < best.d) best = { plot: p.index, d, at };
    }
    return best && candidate(ctx.store, best.plot, best.at);
  },
  run(ctx, c) {
    const owner = ownerOf(ctx.store, Number(c.id.slice(PREFIX.length)));
    if (owner === 'mine') ctx.store.openScreen('mailbox', undefined);
    else if (owner) ctx.store.openScreen('writer', { to: owner });
  },
  fromTap(ctx, hit) {
    const o: WorldObject | undefined = hit.object;
    if (ctx.space !== 'out' || !o || o.kind !== 'mailbox') return null;
    const plot = (o.state as { plot?: number }).plot;
    return typeof plot === 'number' ? candidate(ctx.store, plot, { x: o.x + 0.5, y: o.y + 0.5 }) : null;
  },
};
